import * as React from 'react'
import PdfHandler from '@/components/pdfHandler/PdfHandler'
import { View, Text, StyleSheet, Svg, Line } from '@react-pdf/renderer'
import { IFullSchema } from '@/store/apis/schemas'
import { ITimeslot } from '@/store/apis/timeslots'
import { IUser } from '@/store/apis/scenes'
import { useTranslation } from 'react-i18next'
import moment from 'moment'

const styles = StyleSheet.create({
  title: {
    fontSize: 24,
    textAlign: 'center',
    marginBottom: 20,
  },
  period: {
    fontSize: 12,
    textAlign: 'center',
    marginBottom: 40,
  },
  day: {
    fontSize: 18,
    margin: 12,
    marginBottom: 20,
  },
  dayText: {
    marginBottom: 20,
  },
  timeslot: {
    marginBottom: 20,
  },
  time: {
    marginLeft: 20,
    fontSize: 14,
    textAlign: 'justify',
    fontWeight: 'bold',
  },
  comment: {
    marginTop: 12,
    marginLeft: 20,
    fontSize: 14,
    textAlign: 'justify',
  },
  peopleBusy: {
    marginTop: 12,
    marginLeft: 50,
    fontSize: 14,
    textAlign: 'justify',
  },
  image: {
    marginVertical: 15,
    marginHorizontal: 100,
  },
  pageNumber: {
    position: 'absolute',
    fontSize: 12,
    bottom: 30,
    left: 0,
    right: 0,
    textAlign: 'center',
    color: 'grey',
  },
})

interface IFindUsers {
  includingUsers: string[]
  excludingUsers: string[]
}
const findUsers = (users: IUser[], timeslot: ITimeslot): IFindUsers => {
  const timeslotStart = moment(timeslot.startTimestamp)
  const timeslotEnd = moment(timeslot.endTimestamp)
  const includingUsers: string[] = []
  const excludingUsers: string[] = []
  users.map((user) => {
    const overlappingUsertimeslot = user.timeslots.findIndex((userTimeslot) => {
      const userStart = moment(userTimeslot.startTimestamp)
      const userEnd = moment(userTimeslot.endTimestamp)
      return (
        timeslotStart.isBetween(userStart, userEnd, 'minutes', '[)') ||
        timeslotEnd.isBetween(userStart, userEnd, 'minutes', '(]')
      )
    })
    const userName = `${user.firstName} ${user.lastName}`
    if (overlappingUsertimeslot === -1) {
      includingUsers.push(userName)
    } else {
      excludingUsers.push(userName)
    }
  })
  return {
    includingUsers,
    excludingUsers,
  }
}

const buildFullUserList = (
  schemaUsers: IUser[],
  sceneUsers: { id: number }[],
  timeslot: ITimeslot,
): IUser[] => {
  const foundUsers = schemaUsers.filter(
    (user) =>
      sceneUsers.findIndex((sceneUser) => sceneUser.id === user.id) > -1,
  )
  const includingUsers = timeslot.includingUsers
    ? timeslot.includingUsers.map((userId) => {
        return schemaUsers.find((user) => user.id === userId)
      })
    : []

  const fullUserList = foundUsers.concat(includingUsers)
  const excludingUserIds = timeslot.excludingUsers ?? []
  return fullUserList.filter((user) => !excludingUserIds.includes(user.id))
}

interface ISortKey {
  id: number
  type: 'SCENE' | 'SCENE_GROUP'
  timeslotId: number
  startTimestamp: moment.Moment
}

interface IPdfHandlerProps {
  schema: IFullSchema
  selectedScenes?: number[]
  selectedSceneGroups?: number[]
}

interface IPdfTimeslot {
  startDate: moment.Moment
  endDate: moment.Moment
  name: string
  comment: string
  place?: string
  includingUsers: string
  excludingUsers: string
}

interface IPdfTimeslots {
  day: moment.Moment
  timeslots: IPdfTimeslot[]
}

interface IMappedDay {
  day: string
  sortKeys: ISortKey[]
}

const PdfSchema: React.FC<IPdfHandlerProps> = ({
  schema,
  selectedScenes,
  selectedSceneGroups,
}) => {
  const { t } = useTranslation()
  const [times, setTimes] = React.useState<IPdfTimeslots[]>([])

  React.useEffect(() => {
    const sceneSortKeys: ISortKey[] = schema.scenes.reduce((list, scene) => {
      if (selectedScenes) {
        if (!selectedScenes.includes(scene.scene.id)) {
          return list
        }
      }
      return list.concat(
        scene.timeslots.map((timeslot) => ({
          id: scene.scene.id,
          type: 'SCENE',
          timeslotId: timeslot.id,
          startTimestamp: moment(timeslot.startTimestamp),
          includingUsers: timeslot.includingUsers,
          excludingUsers: timeslot.excludingUsers,
        })),
      )
    }, [])

    const sceneGroupSortKeys: ISortKey[] = schema.sceneGroups.reduce(
      (list, sceneGroup) => {
        if (selectedSceneGroups) {
          if (!selectedSceneGroups.includes(sceneGroup.sceneGroup.id)) {
            return list
          }
        }
        return list.concat(
          sceneGroup.timeslots.map((timeslot) => ({
            id: sceneGroup.sceneGroup.id,
            type: 'SCENE_GROUP',
            timeslotId: timeslot.id,
            startTimestamp: moment(timeslot.startTimestamp),
          })),
        )
      },
      [],
    )

    const sortedKeys = sceneSortKeys
      .concat(sceneGroupSortKeys)
      .sort((a, b) => (a.startTimestamp < b.startTimestamp ? -1 : 1))
    const mappedToDay = sortedKeys.reduce((list, sortKey) => {
      const keyDay = sortKey.startTimestamp.format('MM-DD')
      const foundId = list.map((mappedDay) => mappedDay.day).indexOf(keyDay)
      if (foundId > -1) {
        list[foundId] = {
          day: list[foundId].day,
          sortKeys: [...list[foundId].sortKeys, sortKey],
        }
      } else {
        list.push({
          day: sortKey.startTimestamp.format('MM-DD'),
          sortKeys: [sortKey],
        })
      }
      return list
    }, [] as IMappedDay[])
    const filteredTimes: IPdfTimeslots[] = []
    // TODO: This is slow as hell
    mappedToDay.map((keyDay) => {
      const day = moment(keyDay.day, 'MM-DD')
      if (day.isSameOrAfter(moment())) {
        filteredTimes.push({
          day,
          timeslots: keyDay.sortKeys.reduce((pdfTimeslots, pdfTimeslot) => {
            if (pdfTimeslot.type === 'SCENE') {
              if (selectedScenes) {
                if (!selectedScenes.includes(pdfTimeslot.id)) {
                  return pdfTimeslots
                }
              }
              const scene = schema.scenes.find(
                (scene) => scene.scene.id === pdfTimeslot.id,
              )
              const timeslot = scene.timeslots.find(
                (timeslot) => timeslot.id === pdfTimeslot.timeslotId,
              )
              // TODO: What to do with users that can partially
              const sceneUsers = buildFullUserList(
                schema.users,
                scene.users,
                timeslot,
              )
              const users = findUsers(sceneUsers, timeslot)
              pdfTimeslots.push({
                startDate: moment(timeslot.startTimestamp),
                endDate: moment(timeslot.endTimestamp),
                name: scene.scene.name,
                comment: timeslot.comment,
                place: timeslot.placeName,
                includingUsers: users.includingUsers
                  .sort((a: string, b: string) => (a < b ? -1 : 1))
                  .join(', '),
                excludingUsers: users.excludingUsers
                  .sort((a: string, b: string) => (a < b ? -1 : 1))
                  .join(', '),
              })
            } else {
              if (selectedSceneGroups) {
                if (!selectedSceneGroups.includes(pdfTimeslot.id)) {
                  return pdfTimeslots
                }
              }
              const sceneGroup = schema.sceneGroups.find(
                (sceneGroup) => sceneGroup.sceneGroup.id === pdfTimeslot.id,
              )
              const timeslot = sceneGroup.timeslots.find(
                (timeslot) => timeslot.id === pdfTimeslot.timeslotId,
              )
              let includingUsers: string[] = []
              let excludingUsers: string[] = []
              sceneGroup.scenes.map((sceneGroupScene) => {
                const scene = schema.scenes.find(
                  (scene) => scene.scene.id === sceneGroupScene.id,
                )
                const sceneUsers = buildFullUserList(
                  schema.users,
                  scene.users,
                  timeslot,
                )
                const users = findUsers(sceneUsers, timeslot)
                includingUsers = users.includingUsers
                  .concat(includingUsers)
                  .sort((a: string, b: string) => (a < b ? -1 : 1))
                excludingUsers = users.excludingUsers
                  .concat(excludingUsers)
                  .sort((a: string, b: string) => (a < b ? -1 : 1))
              })
              pdfTimeslots.push({
                startDate: moment(timeslot.startTimestamp),
                endDate: moment(timeslot.endTimestamp),
                name: sceneGroup.sceneGroup.name,
                comment: timeslot.comment,
                place: timeslot.placeName,
                includingUsers: [
                  ...new Set(
                    includingUsers.sort((a: string, b: string) =>
                      a < b ? -1 : 1,
                    ),
                  ),
                ].join(', '),
                excludingUsers: [
                  ...new Set(
                    excludingUsers.sort((a: string, b: string) =>
                      a < b ? -1 : 1,
                    ),
                  ),
                ].join(', '),
              })
            }
            return pdfTimeslots
          }, [] as IPdfTimeslot[]),
        })
      }
      return filteredTimes
    }, [])
    setTimes(filteredTimes)
  }, [schema])

  return (
    <PdfHandler title={`${t('pdf.title')} - ${schema.schema.schema.name}`}>
      <Text style={styles.title}>
        {t('pdf.title')} - {schema.schema.schema.name}
      </Text>
      {/* TODO: Need a way of getting only between two dates */}
      <Text style={styles.period}>
        <>
          {`${t('pdf.period')}: `}
          {moment(schema.schema.schema.startDate).format('MMMM Do YYYY')}
          {' - '}
          {moment(schema.schema.schema.endDate).format('MMMM Do YYYY')}
        </>
      </Text>
      {times.map((time, index) => (
        <View key={index} style={styles.day}>
          <Text style={styles.dayText}>{time.day.format('DD/MM')}</Text>
          {time.timeslots.map((timeslot, index: number) => (
            <View key={index} style={styles.timeslot}>
              <Text style={styles.time}>
                {`${t('pdf.oClock')}: `}
                {moment(timeslot.startDate).format('HH:mm')}
                {' - '}
                {moment(timeslot.endDate).format('HH-mm')}
              </Text>
              <Text style={styles.comment}>
                {`${t('pdf.what')}: `}
                {timeslot.name}
              </Text>
              <Text style={styles.comment}>
                {`${t('pdf.place')}: `}
                {timeslot.place}
              </Text>
              <Text style={styles.comment}>
                {timeslot.comment && (
                  <>
                    {`${t('pdf.info')}: `}
                    {timeslot.comment}
                  </>
                )}
              </Text>
              <Text style={styles.peopleBusy}>
                {timeslot.includingUsers.length ? (
                  <>
                    {timeslot.includingUsers.length +
                      timeslot.excludingUsers.length ===
                    schema.users.length ? (
                      <>{`${t('pdf.called')}: ${t('pdf.all').toUpperCase()}`}</>
                    ) : (
                      <>
                        {`${t('pdf.called')}: `} {timeslot.includingUsers}
                      </>
                    )}
                  </>
                ) : (
                  ''
                )}
              </Text>
              <Text style={styles.peopleBusy}>
                {timeslot.excludingUsers.length ? (
                  <>
                    {`${t('pdf.cant')}: `} {timeslot.excludingUsers}
                  </>
                ) : (
                  ''
                )}
              </Text>
            </View>
          ))}
          <Svg height="10" width="1000">
            <Line
              x1="0"
              y1="5"
              x2="280"
              y2="5"
              strokeWidth={1}
              stroke="rgb(0,0,0)"
            />
          </Svg>
        </View>
      ))}
      <Text
        style={styles.pageNumber}
        render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
        fixed
      />
    </PdfHandler>
  )
}

export default PdfSchema
